import {
  Anchor,
  Badge,
  Button,
  Card,
  Center,
  Divider,
  Group,
  Modal,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconMessage, IconPencil, IconPlus, IconSparkles } from '@tabler/icons-react';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import useFetch from '../../helpers/useFetch';
import Spinner from '../Spinner';
import { NoVectorBadge, PaginationBar, ScreenHint, UnitStatusBadge } from './shared';

const SEGMENT_FILTERS = {
  pending: { reviewStatus: 'draft', synthetic: true },
  approved: { reviewStatus: 'published' },
  all: {},
  manual: { synthetic: false },
};

// The document picker loads one large page rather than paginating; the note below the
// Select warns when a corpus is larger than this so nothing looks silently missing.
const DOC_PICKER_LIMIT = 500;

const provenanceLine = (unit) => {
  if (!unit.provenance) return unit.synthetic ? null : 'hand-authored';
  return `from source passage${unit.provenance.page != null ? ` · p. ${unit.provenance.page}` : ''}`;
};

const QaReviewScreen = ({ apiBase, refreshCounts, onOpenDocument }) => {
  const fetch = useFetch();

  const [segment, setSegment] = useState('pending');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [refreshKey, setRefreshKey] = useState(0);
  const [units, setUnits] = useState(null);
  const [total, setTotal] = useState(0);
  const [pendingTotal, setPendingTotal] = useState(null);
  const [loading, setLoading] = useState(true);

  // hasEmbedding is not returned by the list endpoint — track invalidated vectors per session.
  const [editedIds, setEditedIds] = useState(() => new Set());
  const [editingId, setEditingId] = useState(null);
  const [draftQuestion, setDraftQuestion] = useState('');
  const [draftAnswer, setDraftAnswer] = useState('');
  const [savingId, setSavingId] = useState(null);

  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejecting, setRejecting] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [docs, setDocs] = useState(null);
  const [docsTotal, setDocsTotal] = useState(0);
  const [addDoc, setAddDoc] = useState(null);
  const [addQuestion, setAddQuestion] = useState('');
  const [addAnswer, setAddAnswer] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const query = queryString.stringify({ page, perPage, ...SEGMENT_FILTERS[segment] });
    fetch(`${apiBase}/qa?${query}`)
      .then((response) => {
        if (cancelled) return;
        // Approving/rejecting the last item on a later page empties it — snap back.
        if (!response.units.length && response.total > 0 && page > 1) {
          setPage(1);
          return;
        }
        setUnits(response.units);
        setTotal(response.total);
        if (segment === 'pending') setPendingTotal(response.total);
      })
      .catch((error) => {
        if (cancelled) return;
        notifications.show({ title: 'Failed to load Q&A pairs', message: error.message, color: 'danger' });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetch, apiBase, segment, page, perPage, refreshKey]);

  const refetch = () => {
    setRefreshKey((key) => key + 1);
    refreshCounts();
  };

  const changeSegment = (value) => {
    setSegment(value);
    setPage(1);
    setEditingId(null);
  };

  const approve = (unit) => {
    setSavingId(unit.id);
    fetch(`${apiBase}/units/${unit.id}`, { method: 'PUT', body: JSON.stringify({ reviewStatus: 'published' }) })
      .then(() => {
        notifications.show({
          title: 'Q&A approved',
          message: 'The pair is now live for the assistant.',
          color: 'success',
        });
        refetch();
      })
      .catch((error) => notifications.show({ title: 'Approve failed', message: error.message, color: 'danger' }))
      .finally(() => setSavingId(null));
  };

  const startEdit = (unit) => {
    setEditingId(unit.id);
    setDraftQuestion(unit.question);
    setDraftAnswer(unit.content);
  };

  const saveAndApprove = (unit) => {
    const body = { reviewStatus: 'published' };
    if (draftQuestion !== unit.question) body.question = draftQuestion;
    if (draftAnswer !== unit.content) body.content = draftAnswer;
    const edited = body.question !== undefined || body.content !== undefined;

    setSavingId(unit.id);
    fetch(`${apiBase}/units/${unit.id}`, { method: 'PUT', body: JSON.stringify(body) })
      .then(() => {
        if (edited) {
          setEditedIds((prev) => new Set(prev).add(unit.id));
          notifications.show({
            title: 'Approved with edits',
            message: 'Serving keyword-only until the background re-embed lands (“no vector yet”).',
            color: 'success',
          });
        } else {
          notifications.show({
            title: 'Q&A approved',
            message: 'The pair is now live for the assistant.',
            color: 'success',
          });
        }
        setEditingId(null);
        refetch();
      })
      .catch((error) => notifications.show({ title: 'Approve failed', message: error.message, color: 'danger' }))
      .finally(() => setSavingId(null));
  };

  const confirmReject = () => {
    setRejecting(true);
    fetch(`${apiBase}/units/${rejectTarget.id}`, { method: 'DELETE' })
      .then(() => {
        notifications.show({ title: 'Q&A rejected', message: 'The pair was deleted.', color: 'danger' });
        setRejectTarget(null);
        refetch();
      })
      .catch((error) => notifications.show({ title: 'Reject failed', message: error.message, color: 'danger' }))
      .finally(() => setRejecting(false));
  };

  const openAdd = () => {
    setAddDoc(null);
    setAddQuestion('');
    setAddAnswer('');
    setAddOpen(true);
    fetch(`${apiBase}/documents?perPage=${DOC_PICKER_LIMIT}`)
      .then(({ documents, total }) => {
        setDocs(documents);
        setDocsTotal(total ?? documents.length);
      })
      .catch((error) =>
        notifications.show({ title: 'Failed to load documents', message: error.message, color: 'danger' })
      );
  };

  const createQa = () => {
    setAdding(true);
    fetch(`${apiBase}/documents/${addDoc}/qa`, {
      method: 'POST',
      body: JSON.stringify({ question: addQuestion, answer: addAnswer }),
    })
      .then(() => {
        notifications.show({
          title: 'Q&A pair created',
          message: 'Born draft — approve it to make it live for the assistant.',
          color: 'success',
        });
        setAddOpen(false);
        setAddQuestion('');
        setAddAnswer('');
        setSegment('manual');
        setPage(1);
        refetch();
      })
      .catch((error) => notifications.show({ title: 'Create failed', message: error.message, color: 'danger' }))
      .finally(() => setAdding(false));
  };

  return (
    <div>
      <Group justify="space-between" wrap="wrap" gap={8}>
        <SegmentedControl
          aria-label="Q&A filter"
          value={segment}
          onChange={changeSegment}
          data={[
            { value: 'pending', label: pendingTotal != null ? `Pending · ${pendingTotal}` : 'Pending' },
            { value: 'approved', label: 'Approved' },
            { value: 'all', label: 'All' },
            { value: 'manual', label: 'Manual' },
          ]}
        />
        <Group gap={8} align="center" wrap="nowrap">
          <ScreenHint text="AI-generated questions from your documents wait here as drafts — nothing is served to shoppers until you approve it." />
          <Button leftSection={<IconPlus size={15} stroke={2} />} onClick={openAdd}>
            Add Q&A
          </Button>
        </Group>
      </Group>

      {loading || units === null ? (
        <Spinner />
      ) : units.length === 0 ? (
        <Center py={56}>
          <Stack align="center" gap={8}>
            <IconMessage size={44} stroke={1} color="var(--mantine-color-gray-5)" />
            <Text fz="lg" fw={500}>
              Inbox zero
            </Text>
            <Text size="sm" c="dimmed">
              No Q&A pairs match this filter.
            </Text>
          </Stack>
        </Center>
      ) : (
        <Stack gap={10} mt={14}>
          {units.map((unit) => {
            const isEditing = editingId === unit.id;
            const provenance = provenanceLine(unit);
            return (
              <Card key={unit.id} withBorder radius="sm" padding="md">
                <Card.Section inheritPadding withBorder py="sm">
                  <Group gap={8} wrap="wrap">
                    {unit.locale && (
                      <Badge variant="default" size="sm">
                        {unit.locale.toUpperCase()}
                      </Badge>
                    )}
                    <Badge
                      variant="default"
                      size="sm"
                      tt="none"
                      leftSection={
                        unit.synthetic ? (
                          <IconSparkles size={12} stroke={1.5} />
                        ) : (
                          <IconPencil size={12} stroke={1.5} />
                        )
                      }
                    >
                      {unit.synthetic ? 'synthetic' : 'manual'}
                    </Badge>
                    <UnitStatusBadge status={unit.reviewStatus} documentStatus={unit.knowledgeDocument?.reviewStatus} />
                    {editedIds.has(unit.id) && <NoVectorBadge />}
                    {unit.knowledgeDocument && (
                      <Group gap={6} ml="auto" wrap="nowrap">
                        <Text fz={12} c="dimmed">
                          from
                        </Text>
                        <Anchor fz="xs" component="button" onClick={() => onOpenDocument(unit.knowledgeDocument.id)}>
                          {unit.knowledgeDocument.title}
                        </Anchor>
                      </Group>
                    )}
                  </Group>
                </Card.Section>

                <Stack gap={6} pt={12}>
                  {isEditing ? (
                    <>
                      <Textarea
                        label="Question"
                        autosize
                        minRows={1}
                        value={draftQuestion}
                        onChange={(e) => setDraftQuestion(e.currentTarget.value)}
                      />
                      <Textarea
                        label="Answer"
                        autosize
                        minRows={3}
                        value={draftAnswer}
                        onChange={(e) => setDraftAnswer(e.currentTarget.value)}
                      />
                    </>
                  ) : (
                    <>
                      <Text size="sm" fw={600}>
                        {unit.question}
                      </Text>
                      <Text fz={13} c="gray.7" lh={1.55}>
                        {unit.content}
                      </Text>
                    </>
                  )}
                  {provenance && (
                    <Text fz={12} c="gray.5" fs="italic" mt={2}>
                      {provenance}
                    </Text>
                  )}
                  {unit.reviewStatus === 'draft' && (
                    <Group justify="flex-end" gap={8} mt={4}>
                      {isEditing ? (
                        <>
                          <Button size="xs" variant="default" onClick={() => setEditingId(null)}>
                            Cancel
                          </Button>
                          <Button
                            size="xs"
                            color="success"
                            loading={savingId === unit.id}
                            onClick={() => saveAndApprove(unit)}
                          >
                            Save & approve
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button size="xs" variant="subtle" color="danger" onClick={() => setRejectTarget(unit)}>
                            Reject
                          </Button>
                          <Button size="xs" variant="default" onClick={() => startEdit(unit)}>
                            Edit & approve
                          </Button>
                          <Button
                            size="xs"
                            color="success"
                            leftSection={<IconCheck size={13} stroke={2} />}
                            loading={savingId === unit.id}
                            onClick={() => approve(unit)}
                          >
                            Approve
                          </Button>
                        </>
                      )}
                    </Group>
                  )}
                </Stack>
              </Card>
            );
          })}
        </Stack>
      )}

      {units !== null && total > 0 && (
        <PaginationBar
          shown={units.length}
          total={total}
          noun="pairs"
          page={page}
          onPageChange={setPage}
          perPage={perPage}
          onPerPageChange={(value) => {
            setPerPage(value);
            setPage(1);
          }}
          mt="md"
        />
      )}

      <Modal
        opened={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        title="Reject this Q&A pair?"
        size="md"
        centered
      >
        <Text size="sm" c="dimmed">
          The pair is deleted and will not be suggested again for the same passage.
        </Text>
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={() => setRejectTarget(null)}>
            Cancel
          </Button>
          <Button color="danger" loading={rejecting} onClick={confirmReject}>
            Reject
          </Button>
        </Group>
      </Modal>

      <Modal opened={addOpen} onClose={() => setAddOpen(false)} title="Add Q&A pair" size="lg" centered>
        <Stack gap="sm">
          <Select
            label="Document"
            required
            searchable
            placeholder={docs === null ? 'Loading…' : 'Select a document'}
            description={
              docs && docsTotal > docs.length
                ? // Mantine's `searchable` filters the loaded `data` client-side, so search
                  // can't reach the documents beyond DOC_PICKER_LIMIT — say so plainly.
                  `Showing the first ${docs.length} of ${docsTotal} documents — the rest aren't selectable here yet.`
                : undefined
            }
            data={(docs || []).map((doc) => ({ value: String(doc.id), label: doc.title }))}
            value={addDoc}
            onChange={setAddDoc}
          />
          <TextInput
            label="Question"
            required
            placeholder="What the shopper asks…"
            value={addQuestion}
            onChange={(e) => setAddQuestion(e.currentTarget.value)}
          />
          <Textarea
            label="Answer"
            required
            autosize
            minRows={3}
            placeholder="The answer the assistant should give…"
            value={addAnswer}
            onChange={(e) => setAddAnswer(e.currentTarget.value)}
          />
        </Stack>
        <Text fz={12} c="dimmed" mt={10}>
          Created as a draft under the selected document — approve it in the inbox to make it live.
        </Text>
        <Divider my="md" />
        <Group justify="flex-end" gap={8}>
          <Button variant="default" onClick={() => setAddOpen(false)}>
            Cancel
          </Button>
          <Button
            disabled={!addDoc || !addQuestion.trim() || !addAnswer.trim()}
            loading={adding}
            onClick={createQa}
          >
            Add pair
          </Button>
        </Group>
      </Modal>
    </div>
  );
};

export default QaReviewScreen;
